import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import { MessageIA } from "./Advertisements";
import { getAccountData } from "../taskpane";
import { InfoLabel } from "@fluentui/react-components";

/**
 * Interface définissant la structure d'un message.
 */
interface Message {
  role: "assistant" | "user"; // Rôle de l'expéditeur du message (assistant ou utilisateur)
  content: string; // Contenu du message
  reference?: string; // Référence optionnelle pour le message
}

/**
 * Interface définissant les propriétés du composant GuideLivre.
 */
export interface GuideLivreProps {
  handleMenuReload: () => void; // Fonction pour recharger le menu principal
  setCurrentView: (view: string) => void; // Fonction pour changer la vue actuelle
  askBookQuestion: (request: string) => Promise<string>; // Fonction pour poser une question au guide
}

/**
 * Composant GuideLivre : permet à l'utilisateur de poser des questions sur un manuel Excel
 * et d'obtenir des réponses de l'assistant.
 * @param {GuideLivreProps} props - Propriétés du composant.
 * @returns {JSX.Element} - Le composant React.
 */
const GuideLivre: React.FC<GuideLivreProps> = (props: GuideLivreProps) => {
  // États locaux pour gérer les messages, les entrées utilisateur et les erreurs
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content: "Bonjour, je suis votre assistant, comment puis-je vous aider?",
    },
  ]);
  const [inputValue, setInputValue] = useState(""); // Valeur de l'entrée utilisateur
  const [isLoading, setIsLoading] = useState(false); // Indique si une requête est en cours
  const [error, setError] = useState<string | null>(null); // Message d'erreur
  const [isDisabled, setIsDisabled] = React.useState(true); // Désactive le bouton si l'entrée est invalide
  const [errorMessage, setErrorMessage] = React.useState<string>(""); // Message d'erreur pour l'entrée utilisateur

  /**
   * Retourne les styles pour un message en fonction du rôle (utilisateur ou assistant). (HARDCODE FIX D'UN BUG DARK MODE)
   * @param {("user" | "assistant")} role - Rôle du message.
   * @returns {React.CSSProperties} - Styles CSS pour le message.
   */
  const getMessageStyle = (role: "user" | "assistant") => {
    const commonStyles = {
      padding: "8px 12px",
      borderRadius: "16px",
      margin: "4px 0",
      whiteSpace: "pre-wrap",
      maxWidth: "80%",
    };

    const isDarkMode = document.body.classList.contains("dark");

    if (role === "user") {
      return {
        ...commonStyles,
        marginLeft: "20%",
        alignSelf: "flex-end",
        backgroundColor: isDarkMode ? "#333" : "#dcf8c6",
        color: isDarkMode ? "#fff" : "#000",
      };
    } else {
      return {
        ...commonStyles,
        alignSelf: "flex-start",
        backgroundColor: isDarkMode ? "#444" : "#fff",
        color: isDarkMode ? "#fff" : "#000",
        border: "1px solid #e0e0e0",
      };
    }
  };

  /**
   * Message de chargement affiché pendant que l'assistant génère une réponse.
   */
  const loadingMessage: Message = {
    role: "assistant",
    content: `
      <div className="typing-animation" style="display: flex; align-items: center; justify-content: center;">
        <div className="dot" style="width: 8px; height: 8px; margin: 0 4px; background-color: #0078d4; border-radius: 50%; opacity: 0; animation: typing 1.5s infinite ease-in-out;"></div>
        <div className="dot" style="width: 8px; height: 8px; margin: 0 4px; background-color: #0078d4; border-radius: 50%; opacity: 0; animation: typing 1.5s infinite ease-in-out;"></div>
        <div className="dot" style="width: 8px; height: 8px; margin: 0 4px; background-color: #0078d4; border-radius: 50%; opacity: 0; animation: typing 1.5s infinite ease-in-out;"></div>
      </div>`,
  };

  /**
   * Gère l'entrée utilisateur et envoie une requête à l'assistant.
   */
  const handleUserInput = async () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      role: "user",
      content: inputValue.trim(),
    };

    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setInputValue("");
    setIsLoading(true);
    setError(null);
    setMessages((prevMessages) => [...prevMessages, loadingMessage]);

    try {
      const response = await props.askBookQuestion(userMessage.content);
      const aiMessage: Message = {
        role: "assistant",
        content: `${response}`,
      };

      setMessages((prevMessages) => [
        ...prevMessages.slice(0, -1), // Supprime le message de chargement
        aiMessage,
      ]);
      props.handleMenuReload();
    } catch (error) {
      setError("Une erreur est survenue, veuillez réessayer.");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Réinitialise la conversation en démarrant un nouveau chat.
   */
  const handleNewChat = () => {
    setMessages([
      {
        role: "assistant",
        content: "Bonjour, je suis votre assistant, comment puis-je vous aider?",
      },
    ]);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleUserInput();
    }
  };

  /**
   * Gère la validation de l'entrée utilisateur.
   * @param {React.ChangeEvent<HTMLInputElement>} e - Événement de changement.
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value.trim() === "") {
      setErrorMessage(
        "Votre entrée est vide. L'entrée ne peut pas être vide ni contenir moins de 7 caractères."
      );
      setIsDisabled(true);
    } else if (value.trim().length < 7) {
      setErrorMessage(
        "Veuillez entrer au moins 7 caractères. L'entrée ne peut pas être vide ni contenir moins de 7 caractères."
      );
      setIsDisabled(true);
    } else {
      setErrorMessage("");
      setIsDisabled(false);
    }
  };

  // Rendu du composant
  return (
    <div className="chat-container" style={styles.chatContainer}>
      {/* Informations sur le manuel */}
      <InfoLabel
        info={
          <>
            Ce manuel est une adaptation de Beginning Excel des auteurs Noreen Brown, Barbara Lave,
            Julie Romey, Mary Schatz et Diane Shingledecker. Originalement publié en anglais grâce à
            Open Oregon Educational Resources, cet ouvrage est une introduction au logiciel
            Microsoft Excel en administration des affaires.
          </>
        }
        {...props}
      ></InfoLabel>

      {/* Historique des messages */}
      <div className="chat-history" style={styles.chatHistory}>
        {messages.map((message, index) => (
          <div
            key={index}
            className={`message ${message.role}`}
            style={getMessageStyle(message.role)}
          >
            {/* Affiche le contenu du message */}
            {message.role === "assistant" &&
            message.content.includes('<div className="typing-animation"') ? (
              <div dangerouslySetInnerHTML={{ __html: message.content }} />
            ) : (
              <ReactMarkdown>{message.content}</ReactMarkdown>
            )}
          </div>
        ))}
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="error-message" style={styles.errorMessage}>
          {error}
        </div>
      )}

      {/* Avertissement sur les messages générés par l'IA */}
      <MessageIA />

      {/* Zone de saisie pour l'utilisateur */}
      <div className="chat-input" style={styles.chatInput}>
        <input
          type="text"
          placeholder="Écrivez un message..."
          value={inputValue}
          onChange={(event) => {
            setInputValue(event.target.value);
            handleChange(event);
          }}
          onKeyDown={handleKeyDown}
          style={styles.input}
          disabled={isLoading}
        />
        <button
          className="send-button"
          onClick={handleUserInput}
          style={styles.sendButton}
          disabled={isLoading}
        >
          Envoyer
        </button>
        <button
          className="send-button"
          onClick={handleNewChat}
          style={styles.newChatButton}
          disabled={isLoading}
        >
          Nouveau chat
        </button>
      </div>

      {/* Message d'erreur */}
      {errorMessage && <p style={styles.errorMessage}>{errorMessage}</p>}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  chatContainer: {
    width: "100%",
    margin: "0 auto",
    border: "1px solid #ccc",
    boxSizing: "border-box",
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    height: "60vh",
    fontFamily: "Arial, sans-serif",
  },
  chatHistory: {
    flex: 1,
    padding: "10px",
    overflowY: "auto",
    overflowX: "hidden",
  },
  userMessage: {
    alignSelf: "flex-end",
    padding: "8px 12px",
    borderRadius: "16px",
    margin: "4px 0",
    maxWidth: "50%",
    marginRight: "0px",
    marginLeft: "auto",
    whiteSpace: "pre-wrap",
  },
  assistantMessage: {
    alignSelf: "flex-start",
    padding: "8px 12px",
    borderRadius: "16px",
    margin: "4px 0",
    maxWidth: "80%",
    border: "1px solid #e0e0e0",
  },
  chatInput: {
    display: "flex",
    padding: "10px",
    borderTop: "1px solid #ccc",
  },
  input: {
    flex: 1,
    padding: "8px",
    borderRadius: "4px",
    border: "1px solid #ccc",
    marginRight: "8px",
    fontSize: "16px",
  },
  sendButton: {
    padding: "8px 16px",
    borderRadius: "4px",
    border: "none",
    backgroundColor: "#4CAF50",
    color: "#fff",
    fontSize: "16px",
    cursor: "pointer",
  },
  newChatButton: {
    marginLeft: "8px",
    padding: "8px 16px",
    borderRadius: "4px",
    border: "none",
    backgroundColor: "gray",
    color: "#fff",
    fontSize: "16px",
    cursor: "pointer",
  },
  errorMessage: {
    color: "red",
    textAlign: "center",
    padding: "5px",
  },
};

const spinnerStyle = document.createElement("style");
spinnerStyle.innerHTML = `
@keyframes typing {
  0%, 100% { opacity: 0; }
  50% { opacity: 1; }
}
`;
document.head.appendChild(spinnerStyle);

export default GuideLivre;
